// Prepaid card schema types matching the backend admin REST API.
// See backend/services/api/rest/admin_prepaid_cards.go and
// backend/docs/PREPAID_CARD_DESIGN.md for the contract.
//
// All monetary values are in cents (integer). redeem_days_of_week is a
// bitmask: bit 0 = Sunday ... bit 6 = Saturday, 127 = every day.

export type PrepaidCardSchema = {
  id: string;
  restaurant_id: string;
  name: string;
  description?: string;
  image_url?: string;

  price: number; // pre-tax cents
  tax_free: boolean;
  total_redeem_count: number;

  redeem_start_time: string; // RFC 3339, inclusive
  redeem_end_time: string; // RFC 3339, exclusive
  redeem_days_of_week: number; // bitmask, bit 0 = Sunday ... bit 6 = Saturday

  purchase_start_time: string; // RFC 3339, inclusive
  purchase_end_time: string; // RFC 3339, exclusive

  publish_time?: string; // undefined = draft
  archive_time?: string;

  author_user_id: string;
  create_time: string;
  update_time: string;

  item_schema_ids: string[];
};

export type PrepaidCardSchemaRequest = {
  name: string;
  description?: string;
  image_url?: string;

  price: number;
  tax_free: boolean;
  total_redeem_count: number;

  redeem_start_time: string;
  redeem_end_time: string;
  redeem_days_of_week: number;

  purchase_start_time: string;
  purchase_end_time: string;

  item_schema_ids: string[];
};

// PrepaidCardStat is one schema's aggregate figures over cards purchased
// within a [start_time, end_time) window. See the "Stats (admin)" section of
// PREPAID_CARD_DESIGN.md for the definition of each field.
export type PrepaidCardStat = {
  prepaid_card_schema_id: string;
  name: string;
  published: boolean;

  cards_sold: number;
  cards_pending_payment: number;
  gross_revenue: number; // cents

  counts_sold: number;
  counts_redeemed: number;
  counts_reserved: number;
  counts_outstanding: number;
  counts_expired: number;
  liability: number; // cents
};

export type PrepaidCardStatsResponse = {
  stats: PrepaidCardStat[];
};

// Lightweight item schema info for the supported-items multi-select.
export type ItemSchemaOption = {
  id: string;
  name: string;
};

// Derived, client-side-only status used to drive the list view. The backend
// only stores publish_time / archive_time plus the purchase window; the
// admin-facing lifecycle (Draft / Live / Scheduled / Ended / Archived) is
// computed from those.
export type PrepaidCardSchemaStatus = "DRAFT" | "SCHEDULED" | "LIVE" | "ENDED" | "ARCHIVED";

export function getPrepaidCardSchemaStatus(
  schema: PrepaidCardSchema,
  now: Date = new Date()
): PrepaidCardSchemaStatus {
  if (schema.archive_time) return "ARCHIVED";
  if (!schema.publish_time) return "DRAFT";
  const nowMs = now.getTime();
  if (nowMs < new Date(schema.purchase_start_time).getTime()) return "SCHEDULED";
  if (nowMs < new Date(schema.purchase_end_time).getTime()) return "LIVE";
  return "ENDED";
}

// Bit 0 = Sunday ... bit 6 = Saturday. Must match the backend exactly (see
// prepaid_card_schemas.redeem_days_of_week in PREPAID_CARD_DESIGN.md).
export const ALL_DAYS_OF_WEEK_MASK = 127;

export function daysOfWeekMaskToIndices(mask: number): number[] {
  const days: number[] = [];
  for (let bit = 0; bit < 7; bit++) {
    if ((mask & (1 << bit)) !== 0) {
      days.push(bit);
    }
  }
  return days;
}

export function daysOfWeekIndicesToMask(indices: number[]): number {
  let mask = 0;
  for (const bit of indices) {
    mask |= 1 << bit;
  }
  return mask;
}
